import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import Voices from "@/pages/home/components/Voices";
import { useSEO } from "@/hooks/useSEO";
import { useHashScroll } from "@/hooks/useHashScroll";
import { categoryList, eventList } from "@/utils/content";
import ContactForm from "./components/ContactForm";

const steps = [
  {
    icon: "ri-search-line",
    title: "收集公开信息",
    desc: "把与事件相关的公开帖文、当事方说明与媒体报道按发布时间汇总，先不急着下判断。",
  },
  {
    icon: "ri-sort-desc",
    title: "排出时间顺序",
    desc: "同一时间点、同一件事的信息合并成一个节点，避免时间线出现重复或跳跃。",
  },
  {
    icon: "ri-git-branch-line",
    title: "拆成前因经过结果",
    desc: "把节点分成三段：为什么开始、中间发生了什么、最后形成了什么结果与现状。",
  },
  {
    icon: "ri-refresh-line",
    title: "标注状态并持续更新",
    desc: "为事件标注发酵中、持续跟进或已收尾，有新进展就在原时间线末尾追加节点。",
  },
];

const principles = [
  { icon: "ri-file-list-3-line", title: "只整理公开信息", desc: "不采用无法核实的私下消息，不写没有出处的细节。" },
  { icon: "ri-scales-3-line", title: "不替当事人下结论", desc: "把各方说法并列呈现，判断留给读者自己。" },
  { icon: "ri-time-line", title: "时间写清楚", desc: "能精确到时刻就写时刻，只能到日期就写日期，不虚构具体时间。" },
  { icon: "ri-flag-2-line", title: "状态写明白", desc: "已收尾代表相关方已有明确说明或处理结果，否则标注为持续跟进。" },
  { icon: "ri-edit-2-line", title: "欢迎纠错", desc: "发现时间或事实偏差可以在联系页面反馈，核实后会更新。" },
];

export default function About() {
  useHashScroll();
  const totalNodes = eventList.reduce((sum, item) => sum + item.timeline.length, 0);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于热搜吃瓜",
        description:
          "热搜吃瓜是一个专注热门事件梳理与时间线复盘的中文内容站点，把前因、经过与结果聚合在一起呈现。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "热搜吃瓜",
        url: "https://hot-melon-watch.example.com/",
        email: "contact@hot-melon-watch.example.com",
        description: "专注热门事件梳理与时间线复盘的中文内容站点。",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系热搜吃瓜",
        description: "通过邮件或页面表单提交内容纠错、事件补充与合作投稿。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://hot-melon-watch.example.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "关于我们",
            item: "https://hot-melon-watch.example.com/about",
          },
        ],
      },
    ],
    [],
  );

  useSEO({
    title: "关于我们 | 热搜吃瓜的内容定位与复盘方法",
    description:
      "了解热搜吃瓜是谁：我们把热门事件的前因、经过与结果按时间线聚合，坚持只整理公开信息、标注时间与状态，并欢迎用户纠错与补充。",
    keywords: "热搜吃瓜,关于热搜吃瓜,热搜吃瓜事件梳理,热搜吃瓜复盘方法",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "关于我们" }]} />
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              关于热搜吃瓜
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              我们是一个专注热门事件梳理的内容站点。每一件事，都尽量按时间顺序把来龙去脉讲完整，
              让你在一个页面里就能补齐前因后果，不用在无数条帖子和截图之间来回对照。
            </p>
            <dl className="mt-8 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dt className="text-xs text-foreground-500">已梳理事件</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">
                  {eventList.length}
                </dd>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dt className="text-xs text-foreground-500">时间线节点</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">
                  {totalNodes}
                </dd>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dt className="text-xs text-foreground-500">覆盖分类</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">
                  {categoryList.length}
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <SectionHeading
                eyebrow="网站定位"
                title="我们只做一件事：把事件讲完整"
                desc="热榜更新很快，但真正让人累的往往不是信息少，而是信息散。我们做的事很具体，就是把同一件事的信息收拢到一条时间线上。"
              />
              <div className="flex flex-col gap-4">
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                    <i className="ri-focus-3-line text-lg text-primary-600"></i>
                    面向谁
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                    面向想在几分钟内看懂一件事的普通读者，也面向需要快速复盘选题的内容从业者。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                    <i className="ri-lightbulb-line text-lg text-accent-600"></i>
                    发展理念
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                    与其追着热榜更新，不如把已经发生的事按顺序摆清楚。信息可以快，但整理需要慢一点、准一点。
                  </p>
                </div>
              </div>
            </div>

            <div id="method" className="scroll-mt-32">
              <SectionHeading
                eyebrow="复盘方法"
                title="一件事，分四步理清"
                desc="从收集素材到持续更新，每一步都有明确标准，保证不同事件的时间线结构一致。"
              />
              <ol className="flex flex-col gap-4">
                {steps.map((step, index) => (
                  <li
                    key={step.title}
                    className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${step.icon} text-xl`}></i>
                    </span>
                    <div>
                      <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                        <span className="font-heading text-sm text-background-400">
                          0{index + 1}
                        </span>
                        {step.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                        {step.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full bg-primary-950">
          <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
            <span className="flex w-fit items-center gap-2 rounded-md bg-accent-500/20 px-2.5 py-1 text-xs font-medium tracking-wide text-accent-200">
              <i className="ri-shield-check-line text-sm"></i>
              内容原则
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
              这五条，是我们不打算变的
            </h2>
            <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-primary-800 bg-primary-900/50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-800 text-accent-300">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-background-50">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-background-300">{item.desc}</p>
                </div>
              ))}
              <div className="flex flex-col justify-between rounded-lg border border-primary-700 bg-primary-900/40 p-5">
                <div>
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-arrow-right-up-line text-lg"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-background-50">
                    继续看事件
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-background-200">
                    已经有 {eventList.length} 个事件整理好了完整经过，挑一个感兴趣的看看。
                  </p>
                </div>
                <Link
                  to="/events"
                  className="mt-4 flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  进入瓜田总览
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Voices />

        <section id="contact" className="w-full scroll-mt-32 bg-background-50">
          <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
              <div>
                <SectionHeading
                  eyebrow="联系方式"
                  title="发现疏漏，直接告诉我们"
                  desc="时间写错了、事件有后续、或者内容需要下架，都可以通过下面的方式反馈。"
                />
                <div className="flex flex-col gap-3">
                  <a
                    href="mailto:contact@hot-melon-watch.example.com"
                    rel="nofollow"
                    className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">
                        邮件联系
                      </span>
                      <span className="mt-0.5 block text-sm text-foreground-600">
                        contact@hot-melon-watch.example.com
                      </span>
                    </span>
                  </a>
                  <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-reply-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">
                        回复时间
                      </span>
                      <span className="mt-0.5 block text-sm text-foreground-600">
                        一般在两个工作日内回复
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
                <h3 className="mb-5 font-heading text-lg font-semibold text-foreground-950">
                  在线提交反馈
                </h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}